// Seller plan payback milestone: client contract helpers.
// PLAN-SELLER-PLAN-PAYBACK-MILESTONE Phase 4a. The server (server/helpers/sellerPlanPayback/
// presentation.js) owns eligibility, math and presentation state; fixture parity lives in
// docs/fixtures/seller-plan-payback-v1.json. Everything read from the API is untrusted input:
// it is validated into a plain copy here and never rendered as HTML.

export const PAYBACK_SCHEMA_VERSION = 1;
export const PAYBACK_HELP_PATH = '/help/subscription-payback-milestone';
// Same-page sync: an acknowledgement in the popup refreshes any card mounted beside it.
export const PAYBACK_CHANGED_EVENT = 'mayhem:seller-plan-payback-changed';
export const DEFAULT_HEARTBEAT_SECONDS = 45;
// In-session acknowledgement retry schedule after a transient failure (then the card is the guarantee).
export const ACK_RETRY_DELAYS_MS = [2000, 8000, 30000];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LEASE_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;
const HELP_PATH_RE = /^\/help\/[a-z0-9-]{1,80}$/;
const AVAILABILITY = ['unavailable', 'not-earned', 'earned'];

const isCount = value => Number.isSafeInteger(value) && value >= 0;
const isDateString = value => typeof value === 'string' && !Number.isNaN(Date.parse(value));
const isPlainObject = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const isMilestoneUuid = value => typeof value === 'string' && UUID_RE.test(value);

/** Plain, allowlisted copy of a milestone, or null when anything about it is not version 1. */
export function normalizeMilestone(raw) {
  if (!isPlainObject(raw) || !isMilestoneUuid(raw.uuid)) return null;
  const { coverage, comparisonFees } = raw;
  if (!isPlainObject(coverage) || !isPlainObject(comparisonFees)) return null;
  if (!isDateString(raw.startsAt) || !isDateString(raw.endsAt) || !isDateString(raw.earnedAt)) return null;
  if (Date.parse(raw.startsAt) >= Date.parse(raw.endsAt)) return null;
  if (raw.acknowledgedAt !== null && !isDateString(raw.acknowledgedAt)) return null;
  if (raw.calculationVersion !== 1 || raw.comparisonRateBps !== 2500) return null;
  if (!isCount(raw.salesCents) || !isCount(raw.subscriptionPaidCents)) return null;
  if (raw.subscriptionPaidCents <= 0 || raw.currency !== 'USD') return null;
  // The comparison is the exact rational salesCents / 4; anything else is a different contract.
  if (comparisonFees.denominator !== 4 || comparisonFees.centsNumerator !== raw.salesCents) return null;
  if (!isCount(coverage.manualInvoicePayments) || !isCount(coverage.omittedUndatedOrders)
    || !isCount(coverage.conservativeRefunds)) return null;
  if (typeof raw.isActiveCycle !== 'boolean' || typeof raw.claimable !== 'boolean') return null;
  return {
    uuid: raw.uuid,
    startsAt: raw.startsAt,
    endsAt: raw.endsAt,
    earnedAt: raw.earnedAt,
    acknowledgedAt: raw.acknowledgedAt,
    salesCents: raw.salesCents,
    subscriptionPaidCents: raw.subscriptionPaidCents,
    currency: 'USD',
    coverage: {
      manualInvoicePayments: coverage.manualInvoicePayments,
      omittedUndatedOrders: coverage.omittedUndatedOrders,
      conservativeRefunds: coverage.conservativeRefunds,
    },
    isActiveCycle: raw.isActiveCycle,
    claimable: raw.claimable,
  };
}

/** `data.payback` to a plain copy, or null (absent field, old server, unsupported version, junk). */
export function normalizePayback(raw) {
  if (!isPlainObject(raw) || raw.schemaVersion !== PAYBACK_SCHEMA_VERSION) return null;
  if (!AVAILABILITY.includes(raw.availability)) return null;
  let milestone = null;
  if (raw.milestone !== null && raw.milestone !== undefined) {
    milestone = normalizeMilestone(raw.milestone);
    if (!milestone) return null;
  }
  const helpPath = HELP_PATH_RE.test(raw.links?.helpPath) ? raw.links.helpPath : PAYBACK_HELP_PATH;
  return { availability: raw.availability, milestone, helpPath };
}

/** The popup opens only for the active, earned, unacknowledged cycle the server says is claimable. */
export const isClaimable = payback => Boolean(
  payback?.availability === 'earned' && payback.milestone?.claimable && payback.milestone.isActiveCycle,
);

// ---------------------------------------------------------------------------------------------
// Formatting. Money is integer cents; the comparison keeps quarter-cent precision.

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
export const formatUsdCents = cents => USD.format(cents / 100);

/**
 * 25% of sales is salesCents / 4 cents, so dollars carry up to four decimals. Shown exactly
 * (at least two) so a figure that rounds level with the subscription still explains itself.
 */
export function formatComparisonFees(salesCents) {
  const tenThousandths = BigInt(salesCents) * 25n;
  const whole = tenThousandths / 10000n;
  const fraction = String(tenThousandths % 10000n).padStart(4, '0').replace(/0+$/, '').padEnd(2, '0');
  return `$${whole.toLocaleString('en-US')}.${fraction}`;
}

const DATE = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
// Viewer-local calendar dates, matching the native client. The end is exclusive: it is the renewal date.
export const formatCycleDate = value => DATE.format(new Date(value));
export const formatCycleRange = milestone =>
  `${formatCycleDate(milestone.startsAt)} to ${formatCycleDate(milestone.endsAt)}`;

const plural = (count, one, many) => (count === 1 ? one : many);

/** Plain-language caveats for the calculation detail; empty when nothing was held back. */
export function coverageNotes(coverage) {
  const notes = [];
  const manual = coverage.manualInvoicePayments;
  const undated = coverage.omittedUndatedOrders;
  const refunds = coverage.conservativeRefunds;
  if (manual > 0) {
    notes.push(`${manual} invoice ${plural(manual, 'payment', 'payments')} you recorded yourself `
      + `${plural(manual, 'is', 'are')} included. These are seller-reported and not verified by a payment processor.`);
  }
  if (undated > 0) {
    notes.push(`${undated} ${plural(undated, 'order', 'orders')} without a recorded payment date `
      + `${plural(undated, 'was', 'were')} left out, so your real sales may be higher.`);
  }
  if (refunds > 0) {
    notes.push(`${refunds} ${plural(refunds, 'refund', 'refunds')} `
      + `${plural(refunds, 'was', 'were')} subtracted in full, even where part of the refund covered `
      + 'shipping, tax or fees, so your real sales may be higher.');
  }
  return notes;
}

// Approved copy (root plan decision 9). No competitor names, no profit claim.
export const PAYBACK_COPY = Object.freeze({
  headline: 'Your subscription has paid for itself!',
  historyHeadline: 'Your subscription paid for itself',
  body: 'Compared with a marketplace taking 25% of your sales, your estimated savings this billing '
    + 'cycle now exceed your Mayhem subscription cost.',
  historyBody: 'Compared with a marketplace taking 25% of your sales, your estimated savings in this '
    + 'billing cycle exceeded your Mayhem subscription cost.',
  detailsSummary: 'How we calculated this',
  hypothetical: 'This is a hypothetical comparison. It is not a claim about any particular '
    + "marketplace's fees, it is not your business profit, and it is not a promise of future sales.",
  salesNote: 'Paid merchandise from orders and invoice payments, minus refunds. Shipping, tax and '
    + 'shopper fees are not counted.',
});

// ---------------------------------------------------------------------------------------------
// API client. Classifies on `meta.reason` and status only; error strings are never displayed.

function readLease(raw) {
  if (!isPlainObject(raw) || !LEASE_TOKEN_RE.test(raw.token) || !isDateString(raw.expiresAt)) return null;
  const heartbeat = Number.isInteger(raw.heartbeatSeconds) && raw.heartbeatSeconds >= 5
    && raw.heartbeatSeconds <= 90 ? raw.heartbeatSeconds : DEFAULT_HEARTBEAT_SECONDS;
  return { token: raw.token, expiresAt: raw.expiresAt, heartbeatMs: heartbeat * 1000 };
}

async function call(fetchImpl, url, { method = 'GET', body, keepalive = false } = {}) {
  let response;
  try {
    response = await fetchImpl(url, {
      method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...(keepalive ? { keepalive: true } : {}),
    });
  } catch {
    return { ok: false, status: 0, reason: 'network', transient: true };
  }
  let json = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }
  return { response, json };
}

function refusal({ response, json }) {
  const reason = typeof json?.meta?.reason === 'string' ? json.meta.reason : null;
  const retryAfter = json?.meta?.retryAfterSeconds;
  return {
    ok: false,
    status: response.status,
    reason,
    retryAfterSeconds: Number.isInteger(retryAfter) && retryAfter > 0 ? retryAfter : null,
    transient: response.status >= 500 || response.status === 429,
  };
}

/**
 * `fetch` is the AuthContext wrapper (adds the token header), `apiUrl` its API_URL. The returned
 * client has no state; the hook owns lease tokens and never exposes them to rendering.
 */
export function createPaybackClient({ fetch: fetchImpl, apiUrl }) {
  const base = `${apiUrl}/v1/seller/payments/plan-billing`;

  const act = async (action, uuid, leaseToken, options = {}) => {
    if (!isMilestoneUuid(uuid)) return { ok: false, status: 0, reason: 'invalid-milestone', transient: false };
    const result = await call(fetchImpl, `${base}/payback/${uuid}/${action}`, {
      method: 'POST',
      body: { ...(leaseToken ? { leaseToken } : {}) },
      keepalive: options.keepalive,
    });
    if (!result.response) return result;
    if (!result.response.ok || result.json?.meta?.success !== true) return refusal(result);
    const payback = normalizePayback(result.json?.data?.payback);
    const outcome = typeof result.json.meta.outcome === 'string' ? result.json.meta.outcome : null;
    if (action === 'claim') {
      const lease = readLease(result.json?.data?.lease);
      if (!lease) return { ok: false, status: result.response.status, reason: 'malformed', transient: true };
      return { ok: true, outcome, payback, lease };
    }
    return { ok: true, outcome, payback };
  };

  return {
    /** GET with the smallest history page; the billing read model carries `data.payback`. */
    async read() {
      const result = await call(fetchImpl, `${base}?limit=1&offset=0`);
      if (!result.response) return result;
      if (!result.response.ok) return refusal(result);
      return { ok: true, payback: normalizePayback(result.json?.data?.payback) };
    },
    claim: (uuid, leaseToken = null) => act('claim', uuid, leaseToken),
    acknowledge: (uuid, leaseToken) => act('acknowledge', uuid, leaseToken),
    release: (uuid, leaseToken, options) => act('release', uuid, leaseToken, options),
  };
}
